import React from 'react';
import { z } from 'zod';
import { safeUrl } from '../url';
import { styled } from 'styled-components';
import type { BlockDefinition } from '../types';

// Trial CTA — Strapi sections.trial-cta + atoms.pointers. A promo band with a
// composed headline (start + highlighted + end), an optional exclusive badge, a
// bullet list of points, notes, and a CTA button.
export const trialCtaSchema = z.object({
  startText: z.string().optional(),
  highlight: z.string().optional(),
  endText: z.string().optional(),
  exclusiveText: z.string().optional(),
  notes: z.string().optional(),
  points: z.array(z.string()).default([]),
  cta: z.object({ label: z.string(), href: safeUrl }).optional(),
});

type TrialCtaProps = z.infer<typeof trialCtaSchema>;

const Band = styled.section`
  max-width: 112rem;
  margin: 4rem auto;
  padding: 4rem 2rem;
  border-radius: 2.4rem;
  color: #ffffff;
  text-align: left;
  background: linear-gradient(93deg, #063091 0%, #0a3aa8 100%);

  @media (min-width: 64em) {
    padding: 6.4rem;
  }
`;

// Two-column on desktop: headline + CTA on the left, exclusive features right
// (matches the live pricing page). Single column on mobile.
const Inner = styled.div`
  display: grid;
  gap: 3.2rem;

  @media (min-width: 64em) {
    grid-template-columns: 1.05fr 0.95fr;
    gap: 6rem;
    align-items: center;
  }
`;
const Col = styled.div``;

const Badge = styled.span`
  display: inline-block;
  margin-bottom: 1.6rem;
  padding: 0.4rem 1.2rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  font-family: 'DM Sans', sans-serif;
  font-size: 1.3rem;
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const Headline = styled.h2`
  margin: 0;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: clamp(2.6rem, 4vw, 4.4rem);
  line-height: 1.15;

  em {
    font-style: normal;
    color: #9cc0ff;
  }
`;

const Points = styled.ul`
  list-style: none;
  display: inline-grid;
  gap: 0.8rem;
  margin: 2.4rem 0 0;
  padding: 0;
  text-align: left;
`;

const Point = styled.li`
  display: flex;
  gap: 0.8rem;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;

  &::before {
    content: '✓';
    color: #9cc0ff;
    font-weight: 700;
  }
`;

const Cta = styled.a`
  display: inline-block;
  margin-top: 2.4rem;
  padding: 1.3rem 2.8rem;
  border-radius: 1.6rem;
  background: #ffffff;
  color: #063091;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
  font-weight: 600;
  text-decoration: none;
`;

const Notes = styled.p`
  margin: 1.6rem 0 0;
  color: rgba(255, 255, 255, 0.75);
  font-family: 'DM Sans', sans-serif;
  font-size: 1.3rem;
`;

function TrialCta({ startText, highlight, endText, exclusiveText, notes, points, cta }: TrialCtaProps) {
  return (
    <Band>
      <Inner>
        <Col>
          <Headline>
            {startText ? `${startText} ` : null}
            {highlight ? <em>{highlight}</em> : null}
            {endText ? ` ${endText}` : null}
          </Headline>
          {cta ? (
            <div>
              <Cta href={cta.href} target="_blank" rel="noreferrer">
                {cta.label}
              </Cta>
            </div>
          ) : null}
          {notes ? <Notes>{notes}</Notes> : null}
        </Col>
        <Col>
          {exclusiveText ? <Badge>{exclusiveText}</Badge> : null}
          {points.length > 0 ? (
            <Points>
              {points.map((p, i) => (
                <Point key={i}>{p}</Point>
              ))}
            </Points>
          ) : null}
        </Col>
      </Inner>
    </Band>
  );
}

export const trialCtaBlock: BlockDefinition<TrialCtaProps> = {
  type: 'trialCta',
  schema: trialCtaSchema,
  Renderer: TrialCta,
};
